import { useEffect, useRef, type KeyboardEvent, type PointerEvent, type WheelEvent } from "react";
import { Button } from "../components/index.ts";
import { moveSquare, zoomSquare, type Square } from "./face-boxes.ts";
import type { PhotoSource } from "./photo-file.ts";

/**
 * The profile photo as people will see it (a circle), with the controls to fix it: drag the photo
 * to move it, "Closer" and "Wider" (or the mouse wheel) to zoom, the arrow keys to move it with the
 * keyboard. The square never leaves the photo and never gets smaller than `minSide`.
 */
export function CropAdjust({
  source,
  square,
  minSide,
  onChange,
  size = 200,
}: {
  source: PhotoSource;
  square: Square;
  /** The smallest square, in photo pixels: about the size of the face. */
  minSide: number;
  onChange: (square: Square) => void;
  /** The circle's size on screen, in CSS pixels. */
  size?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drag = useRef<{ x: number; y: number; square: Square } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    const scale = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(size * scale);
    canvas.height = Math.round(size * scale);
    context.imageSmoothingQuality = "high";
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(source.canvas, square.x, square.y, square.side, square.side, 0, 0, canvas.width, canvas.height);
  }, [source, square, size]);

  const zoom = (factor: number) => onChange(zoomSquare(square, factor, source.width, source.height, minSide));
  const move = (dx: number, dy: number) => onChange(moveSquare(square, dx, dy, source.width, source.height));
  /** Photo pixels for one screen pixel. */
  const perPixel = square.side / size;

  function onPointerDown(event: PointerEvent<HTMLCanvasElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { x: event.clientX, y: event.clientY, square };
  }

  function onPointerMove(event: PointerEvent<HTMLCanvasElement>) {
    const start = drag.current;
    if (!start) return;
    // Drag the photo, not the frame: the photo follows the pointer, so the square moves the other way.
    const dx = -(event.clientX - start.x) * (start.square.side / size);
    const dy = -(event.clientY - start.y) * (start.square.side / size);
    onChange(moveSquare(start.square, dx, dy, source.width, source.height));
  }

  function onWheel(event: WheelEvent<HTMLCanvasElement>) {
    event.preventDefault();
    zoom(event.deltaY > 0 ? 1.08 : 1 / 1.08);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const step = Math.max(2, Math.round(perPixel * 8));
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const delta = moves[event.key];
    if (delta) {
      event.preventDefault();
      move(delta[0], delta[1]);
    } else if (event.key === "+" || event.key === "=") {
      event.preventDefault();
      zoom(1 / 1.15);
    } else if (event.key === "-") {
      event.preventDefault();
      zoom(1.15);
    }
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <button
        type="button"
        aria-label="Photo position. Drag the photo, or use the arrow keys to move it and + or - to zoom."
        onKeyDown={onKeyDown}
        className="rounded-full outline-none ring-offset-2 focus-visible:ring-4 focus-visible:ring-accent-soft"
      >
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={() => (drag.current = null)}
          onPointerCancel={() => (drag.current = null)}
          onWheel={onWheel}
          className="block cursor-grab touch-none rounded-full bg-sunken shadow-[0_0_0_1px_var(--color-line)] active:cursor-grabbing"
          style={{ width: size, height: size }}
        />
      </button>
      <div className="flex gap-2">
        <Button variant="secondary" type="button" onClick={() => zoom(1 / 1.15)} disabled={square.side <= minSide + 1}>
          Closer
        </Button>
        <Button
          variant="secondary"
          type="button"
          onClick={() => zoom(1.15)}
          disabled={square.side >= Math.min(source.width, source.height) - 1}
        >
          Wider
        </Button>
      </div>
    </div>
  );
}
